"use strict";
/**
 * 审计动作码与操作人类型
 * -----------------------------------------------------------------------------
 * **必须与 docs/06-ops/03-观测与审计.md §2.1「必须审计的动作」表格一致。**
 * 服务端写入：`AuditService.log({ action: AuditAction.PII_VIEW, ... })`
 * 后台筛选：`GET /api/admin/system/audit-logs?action=pii.view`
 *
 * 放 `shared` 的原因与权限码相同：前后端必须用同一份字面量，
 * 否则后台筛选条件会与写入口径漂移（前端传 `pii_view`、服务端存 `pii.view`）。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.AUDIT_ACTIONS_REQUIRING_PURPOSE = exports.AuditOperator = exports.AuditAction = void 0;
/** 必须审计的动作码（§2.1）。新增前先改文档，再改此处。 */
exports.AuditAction = {
    /** 查看完整敏感信息（身份证号 / 手机号 / 影像原图），必须记录用途 */
    PII_VIEW: 'pii.view',
    /** 报名单状态流转（含前状态、后状态、触发原因） */
    REGISTRATION_TRANSITION: 'registration.transition',
    /** 作废报名单 */
    REGISTRATION_VOID: 'registration.void',
    /** 后台编辑报名单字段 */
    REGISTRATION_EDIT: 'registration.edit',
    /** 开课状态设置（单个 / 批量；C-22 独立维度，与主状态机解耦） */
    REGISTRATION_COURSE_STATUS_SET: 'registration.course_status.set',
    /** 报名单人工驳回（含原因）—— 核验前移后不存在「审核通过」动作 */
    REVIEW_REJECT: 'review.reject',
    /**
     * 资料补交判定（含原因与缺项清单 / 放行的复审字段）。
     *
     * 与 `REGISTRATION_EDIT` 分开的理由是**可检索性**：补交判定是「后台主动要求学员补材料」，
     * 与「后台顺手改了个字段」在审计上是两件事。合成一个动作码后，
     * 按动作码过滤会同时捞出大量无关的字段编辑，等于没有这条审计。
     */
    REVIEW_SUPPLEMENT: 'review.supplement',
    /** 人工认领核销 */
    PAYMENT_RECONCILE: 'payment.reconcile',
    /** 撤销核销 */
    PAYMENT_REVOKE: 'payment.revoke',
    /** 退费登记 */
    REFUND_CREATE: 'refund.create',
    /** 开票登记（人工开具后登记） */
    INVOICE_CREATE: 'invoice.create',
    /** 学员提交开票申请（学员侧动作，操作人为 SESSION） */
    INVOICE_APPLY: 'invoice.apply',
    /** 驳回开票申请（带驳回原因） */
    INVOICE_REJECT: 'invoice.reject',
    /** 发货登记 */
    LOGISTICS_SHIP: 'logistics.ship',
    /** 单号与物流状态修改 */
    LOGISTICS_UPDATE: 'logistics.update',
    /** 准入通过线调整（含旧值新值） */
    CONFIG_THRESHOLD_UPDATE: 'config.threshold.update',
    /** 表单 Schema 发布 */
    CONFIG_FORM_PUBLISH: 'config.form.publish',
    /**
     * 通知渠道策略调整（后台「通知渠道管理」保存）。
     *
     * 为什么单列一个动作码而不是复用 `config.form.publish` / `config.threshold.update`：
     * 这条动作改变的是一批事件的**触达方式**（发不发短信、发不发公众号），
     * 直接决定学员会不会收到通知。按动作码能一眼捞出「谁在什么时候把哪条通知的短信关了」，
     * 混进其它配置动作里就查不出来。
     */
    NOTIFY_POLICY_UPDATE: 'notify.policy.update',
    /**
     * 收款账户信息调整（后台「收款账户配置」保存）。
     *
     * 为什么单列：这条配置直接决定学员往哪个账号转账 —— 写错一个账号，
     * 学员的钱就进了错误账户，且往往要到「流水对不上」才发现。
     * 按动作码能一键捞出「谁在什么时候改成了什么账号」，是财务对账的依据。
     */
    PAYMENT_PAYEE_UPDATE: 'payment.payee.update',
    /** 角色权限调整 */
    SYSTEM_ROLE_UPDATE: 'system.role.update',
    /** 新建自定义角色 */
    SYSTEM_ROLE_CREATE: 'system.role.create',
    /** 删除自定义角色 */
    SYSTEM_ROLE_DELETE: 'system.role.delete',
    /** 后台账号增删改（文档表格里的 `system.admin.*` 展开为下列各条） */
    SYSTEM_ADMIN_CREATE: 'system.admin.create',
    SYSTEM_ADMIN_UPDATE: 'system.admin.update',
    SYSTEM_ADMIN_DISABLE: 'system.admin.disable',
    SYSTEM_ADMIN_ENABLE: 'system.admin.enable',
    /** 重置密码：只记事件，**不记任何口令相关内容** */
    SYSTEM_ADMIN_RESET_PASSWORD: 'system.admin.reset_password',
    /** 踢下线：吊销目标账号全部会话 */
    SYSTEM_ADMIN_REVOKE_SESSIONS: 'system.admin.revoke_sessions',
    /** 数据导出 */
    DATA_EXPORT: 'data.export',
    /** 留存清理任务执行结果 */
    RETENTION_PURGE: 'retention.purge',
};
/**
 * 操作人类型。与 `prisma/schema.prisma` 的 `AuditOperatorType` 枚举逐值对应
 * （服务端落库时映射成 Prisma 枚举；本常量供前后端共用）。
 */
exports.AuditOperator = {
    /** 学员 */
    USER: 'USER',
    /** 后台账号 */
    ADMIN: 'ADMIN',
    /** 系统（定时任务、回调） */
    SYSTEM: 'SYSTEM',
};
/**
 * 写入审计时必须显式给出 `purpose` 的动作码。
 * 前端据此强制弹出「请填用途」输入框（§2.3「敏感查看必须记录用途」）。
 */
exports.AUDIT_ACTIONS_REQUIRING_PURPOSE = [exports.AuditAction.PII_VIEW];
//# sourceMappingURL=audit.js.map